/* ==================================================================
   Header visitor location — resolves the visitor's actual location
   using HTML5 Browser Geolocation + Google Maps Geocoder with fallback
   to IP-based server lookup.
   
   Standard conventions:
   - Australia: Resolves Suburb (locality) and State (administrative_area_level_1)
   - India / International: Resolves City (locality) and State (administrative_area_level_1)
   - Caches for 24 hours.
================================================================== */

(function () {
  'use strict';

  const GEO_COOKIE_NAME = 'diversability_geo_v3';
  const GEOCODE_POLL_INTERVAL_MS = 150;
  const GEOCODE_TIMEOUT_MS = 3000;

  let geoResult = null; // cached in-memory once resolved this page load
  let geoCallbacks = [];
  let geoRequestInFlight = false;

  function readGeoCookie() {
    const regex = new RegExp(`(?:^|; )${GEO_COOKIE_NAME}=([^;]*)`);
    const match = regex.exec(document.cookie);
    if (!match?.[1]) {
      return null;
    }

    try {
      return JSON.parse(decodeURIComponent(match[1]));
    } catch (_err) {
      return null;
    }
  }

  function writeGeoCookie(data) {
    const maxAge = 60 * 60 * 24; // 24h
    document.cookie = `${GEO_COOKIE_NAME}=${encodeURIComponent(JSON.stringify(data))}; path=/; max-age=${maxAge}`;
  }

  function flushGeoCallbacks(result) {
    geoCallbacks.forEach(function (cb) {
      try {
        cb(result);
      } catch (e) {
        console.info('Geo callback error:', e);
      }
    });
    geoCallbacks = [];
  }

  /* ================== IP-BASED FALLBACK ================== */
  function fetchIpLocation() {
    if (typeof ajax_callback_data === 'undefined') {
      geoResult = { success: false };
      flushGeoCallbacks(geoResult);
      return;
    }

    const xhr = new XMLHttpRequest();
    xhr.open('POST', ajax_callback_data.ajax_url, true);
    xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');

    xhr.onload = function () {
      let res = { success: false };
      if (xhr.status === 200) {
        try {
          res = JSON.parse(xhr.responseText);
        } catch (_err) {
          res = { success: false };
        }
      }

      if (res.success) {
        res.source = 'ip';
        writeGeoCookie(res);
      }

      geoResult = res;
      flushGeoCallbacks(res);
    };

    xhr.onerror = function () {
      geoResult = { success: false };
      flushGeoCallbacks(geoResult);
    };

    xhr.send('action=qcs_get_visitor_location');
  }

  /* ================== REVERSE GEOCODING ================== */
  function updateLocationFromComponent(comp, locData) {
    const types = comp?.types || [];

    // Priority 1: Official Locality (Suburb in AU, City in IN/US/global)
    if (!locData.locality && types.includes('locality')) {
      locData.locality = comp.long_name;
    }

    // Priority 2: Postal town (UK standard)
    if (!locData.postalTown && types.includes('postal_town')) {
      locData.postalTown = comp.long_name;
    }

    // Priority 3: Fallback sublocality only if no official locality exists
    if (!locData.sublocality && (types.includes('sublocality_level_1') || types.includes('sublocality'))) {
      locData.sublocality = comp.long_name;
    }

    // Priority 4: Admin area level 2 (district/county fallback)
    if (!locData.admin2 && types.includes('administrative_area_level_2')) {
      locData.admin2 = comp.long_name;
    }

    // State / Province / Territory
    if (!locData.region && types.includes('administrative_area_level_1')) {
      locData.region = comp.long_name;
    }
  }

  /**
   * Extracts the standard City/Suburb and State from Google Geocoder results.
   * Google Maps Address Component Hierarchy:
   * - 'locality': The official Suburb in Australia (AS4590 standard) and City in India/US/Europe.
   * - 'postal_town': Used in UK for city/town naming.
   * - 'administrative_area_level_1': Standard State/Province/Territory globally.
   * Sublocalities, neighborhoods, and society names are excluded from primary selection.
   */
  function extractCityAndRegionFromGoogle(results) {
    const locData = {
      locality: '',
      postalTown: '',
      sublocality: '',
      admin2: '',
      region: ''
    };

    for (const result of results) {
      const components = result?.address_components || [];
      for (const comp of components) {
        updateLocationFromComponent(comp, locData);
      }

      if (locData.locality && locData.region) {
        break;
      }
    }

    // Locality is strictly prioritized over society/building/sublocality
    const city = locData.locality || locData.postalTown || locData.sublocality || locData.admin2;
    const region = locData.region;

    return { city, region };
  }

  function handleGeocodeResult(results, status, lat, lng, callback) {
    if (status !== 'OK' || !results?.length) {
      callback(null);
      return;
    }

    const extracted = extractCityAndRegionFromGoogle(results);
    if (!extracted?.city) {
      callback(null);
      return;
    }

    callback({
      success: true,
      city: extracted.city,
      region: extracted.region,
      lat,
      lng,
      source: 'device'
    });
  }

  function reverseGeocodeWithGoogle(lat, lng, callback) {
    function tryGeocode() {
      if (typeof google !== 'object' || typeof google.maps !== 'object' || typeof google.maps.Geocoder !== 'function') {
        return false;
      }

      try {
        const geocoder = new google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, function (results, status) {
          handleGeocodeResult(results, status, lat, lng, callback);
        });
        return true;
      } catch (e) {
        console.info('Reverse geocode error:', e);
        callback(null);
        return true;
      }
    }

    if (tryGeocode()) {
      return;
    }

    let elapsed = 0;
    const interval = setInterval(function () {
      elapsed += GEOCODE_POLL_INTERVAL_MS;
      if (tryGeocode()) {
        clearInterval(interval);
        return;
      }
      if (elapsed >= GEOCODE_TIMEOUT_MS) {
        clearInterval(interval);
        callback(null);
      }
    }, GEOCODE_POLL_INTERVAL_MS);
  }

  function fallbackReverseGeocode(lat, lng, callback) {
    const langMatch = /(?:^|;\s*)diversability_lang=([^;]*)/.exec(document.cookie);
    const localityLanguage = langMatch ? decodeURIComponent(langMatch[1]) : 'en';
    const url = `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${encodeURIComponent(lat)}&longitude=${encodeURIComponent(lng)}&localityLanguage=${encodeURIComponent(localityLanguage)}`;
    fetch(url)
      .then(function (res) { return res.json(); })
      .then(function (data) {
        if (data) {
          let city = '';
          // In Australia, locality = Suburb; in India/elsewhere, city = City
          if (data.countryCode === 'AU') {
            city = data.locality || data.city;
          } else {
            city = data.city || data.locality;
          }

          if (city) {
            callback({
              success: true,
              city,
              region: data.principalSubdivision || '',
              lat,
              lng,
              source: 'device'
            });
            return;
          }
        }
        callback(null);
      })
      .catch(function () {
        callback(null);
      });
  }

  function resolveCoordinates(lat, lng) {
    reverseGeocodeWithGoogle(lat, lng, function (res) {
      if (res?.city) {
        writeGeoCookie(res);
        geoResult = res;
        flushGeoCallbacks(res);
        return;
      }

      // Secondary fallback in case Google Geocoder API is unavailable on this key
      fallbackReverseGeocode(lat, lng, function (fallbackRes) {
        if (fallbackRes?.city) {
          writeGeoCookie(fallbackRes);
          geoResult = fallbackRes;
          flushGeoCallbacks(fallbackRes);
          return;
        }

        // Final fallback to IP-based lookup
        fetchIpLocation();
      });
    });
  }

  /* ================== SHARED ACCESSOR ================== */
  window.qcsGetVisitorGeo = function (callback) {
    if (geoResult !== null) {
      callback(geoResult);
      return;
    }

    const cookieData = readGeoCookie();
    if (cookieData?.success) {
      geoResult = cookieData;
      callback(geoResult);
      return;
    }

    geoCallbacks.push(callback);

    if (geoRequestInFlight) {
      return;
    }
    geoRequestInFlight = true;

    // Clean up legacy cookies from previous iterations
    document.cookie = 'diversability_geo=; path=/; max-age=0';
    document.cookie = 'diversability_geo_v2=; path=/; max-age=0';

    // Attempt browser geolocation if supported
    const isSecure = window.isSecureContext || location.hostname === 'localhost' || location.hostname === '127.0.0.1';
    if (navigator.geolocation && isSecure) {
      navigator.geolocation.getCurrentPosition(
        function (position) {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          resolveCoordinates(lat, lng);
        },
        function (_err) {
          // User denied, timeout, or position unavailable -> Fall back to IP lookup
          fetchIpLocation();
        },
        {
          timeout: 8000,
          maximumAge: 600000,
          enableHighAccuracy: false
        }
      );
    } else {
      // Insecure origin or geolocation not supported -> Fall back to IP lookup
      fetchIpLocation();
    }
  };

  document.addEventListener('DOMContentLoaded', function () {
    const geoEl = document.getElementById('header-geo-location');
    if (!geoEl) {
      return;
    }

    window.qcsGetVisitorGeo(function (result) {
      if (!result?.success || !result?.city) {
        return;
      }

      let locationText = result.city;
      if (result.region) {
        locationText = `${result.city}, ${result.region}`;
      }
      geoEl.textContent = locationText;
    });
  });
})();
